import React, { type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CircleExclamation, Lock } from 'libs/design-system/src/assets/Icons';
import { PageStatus, SkeletonLoader } from '@unified-checkout/design-system';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { useLangStore } from '@app/dashboard/store/lang';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { getSingleOrderDetails } from '@app/dashboard/services/inner_layout/orders/single-order';
import { InfoCard } from '@app/dashboard/components/inner_layout/Cards/InfoCard';
import { getBoolName } from '@app/dashboard/utils/getBoolName';
import { Header } from './Header';
import styles from './single-order.module.scss';

const EMPTY_VALUE = '-';

export const SingleOrder = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const screenWidth = useScreenWidth();
  const { lang } = useLangStore();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const {
    isLoading,
    isError,
    data: response,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SINGLE_ORDER_DETAILS, lang],
    queryFn: () => getSingleOrderDetails(id as string),
    enabled: !!id,
    refetchOnMount: 'always',
  });

  const singleOrderDetails = response?.data;

  const { shipping_data, items } = singleOrderDetails || {};

  const detailsBoxRenderer = ({
    box = undefined,
    height,
    noOfRows,
    key,
  }: {
    box: ReactNode;
    height: string;
    noOfRows: number;
    key: number | string;
  }): ReactNode => {
    return isLoading || isFetching ? (
      <SkeletonLoader
        type="bordered_box"
        noOfRows={noOfRows}
        height={height}
        key={key}
      />
    ) : (
      box && <React.Fragment key={key}>{box}</React.Fragment>
    );
  };

  const getFullName = (shippingData: { fullName?: string; first_name?: string; last_name?: string }) => {
    if (!shippingData) return EMPTY_VALUE;

    const { fullName, first_name, last_name } = shippingData;

    if (fullName) {
      return fullName;
    }

    if (first_name || last_name) {
      return `${first_name || ''}${last_name ? ' ' + last_name : ''}`;
    }
  }

  const allBoxes = [
    detailsBoxRenderer({
      box: singleOrderDetails && (
        <InfoCard
          title={t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.details.title')}
          noOfColumns={2}
          pairsList={[
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.details.merchantOrderId'),
              value: singleOrderDetails.merchant_order_id || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.details.createdBy'),
              value: singleOrderDetails.merchant_staff_tag || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.details.source'),
              value: singleOrderDetails.api_source || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.details.deliveryNeeded'),
              value: getBoolName(t, shipping_data?.delivery_needed) || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.details.collector'),
              value: singleOrderDetails.collector || EMPTY_VALUE,
            },
          ]}
        />
      ),
      noOfRows: 6,
      height: '20rem',
      key: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.details.title'),
    }),
    detailsBoxRenderer({
      box: items?.length && (
        <InfoCard
          title={t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.cart.title')}
          noOfColumns={2}
          pairsList={items.flatMap((item: any) => [
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.cart.productName'),
              value: item.name || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.cart.productDescription'),
              value: item.description || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.cart.quantity'),
              value: item.quantity || EMPTY_VALUE,
            },
          ])}
        />
      ),
      noOfRows: 6,
      height: '20rem',
      key: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.items.title'),
    }),
    detailsBoxRenderer({
      box: shipping_data && (
        <InfoCard
          title={t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.shipping.title')}
          noOfColumns={2}
          pairsList={[
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.shipping.orderId'),
              value: shipping_data.order_id || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.shipping.street'),
              value: shipping_data.street || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.shipping.building'),
              value: shipping_data.building || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.shipping.floor'),
              value: shipping_data.floor || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.shipping.apartment'),
              value: shipping_data.apartment || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.shipping.city'),
              value: shipping_data.city || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.shipping.state'),
              value: shipping_data.state || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.shipping.country'),
              value: shipping_data.country || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.shipping.postalCode'),
              value: shipping_data.postal_code || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.shipping.extraDescription'),
              value: shipping_data.extra_description || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.shipping.shippingMethod'),
              value: shipping_data.shipping_method || EMPTY_VALUE,
            },
          ]}
        />
      ),
      noOfRows: 6,
      height: '20rem',
      key: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.shipping.title'),
    }),
    detailsBoxRenderer({
      box: shipping_data && (
        <InfoCard
          title={t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.client.title')}
          noOfColumns={2}
          pairsList={[
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.client.fullName'),
              value: getFullName(shipping_data) || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.client.email'),
              value: shipping_data.email || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.client.phone'),
              value: shipping_data.phone_number || EMPTY_VALUE,
            },
          ]}
        />
      ),
      noOfRows: 6,
      height: '20rem',
      key: t('innerLayout.ordersPage.singleOrdersPage.detailsBoxes.client.title'),
    }),
  ];

  // Filter out empty boxes
  const filteredBoxes = allBoxes.filter(Boolean);

  // Split the boxes into two columns
  const col1Boxes = filteredBoxes.filter((_, index) => index % 2 === 0); // Even-indexed items
  const col2Boxes = filteredBoxes.filter((_, index) => index % 2 !== 0); // Odd-indexed items

  const content = (
    <>
      {isMobile ? (
        <>
          {col1Boxes}
          {col2Boxes}
        </>
      ) : (
        [col1Boxes, col2Boxes].map((colContent, index) => (
          <div
            className={styles['single-transaction__main__col']}
            key={`single-transaction-col-${index}`}
          >
            {colContent}
          </div>
        ))
      )}
    </>
  );

  return (
    <div className={styles['single-transaction']}>
      {isError ? (
        <PageStatus
          status="error"
          title={t('innerLayout.errors.smthWentWrong')}
          description={t('innerLayout.errors.dataRetrievalErr')}
          icon={CircleExclamation}
        />
      ) : (
        <>
          <Header
            id={Number(id)}
            data={singleOrderDetails}
            isLoading={isLoading || isFetching}
            isError={isError}
            refetchData={refetch}
          />

          <div className={styles['single-transaction__main']}>{content}</div>
        </>
      )}
    </div>
  );
};
